import { ApiResult, HttpClient } from "../http.js";
import { DocumentDto, DocumentFolderDto, DocumentVisibility } from "../types/documents.js";

/**
 * Caller-built multipart/form-data body for document upload.
 *
 * All metadata (entityType, entityId, category, etc.) must be appended as
 * form fields **before** passing this to `upload()` — the api-client sends the
 * FormData as-is so it stays DOM-free and React Native-safe. Required fields:
 *
 *   formData.append("file", <file>)          // required
 *   formData.append("entityType", "project") // required
 *   formData.append("entityId", "<uuid>")    // required
 *   formData.append("category", "...")       // required
 *   formData.append("visibility", "internal" | "shared")  // optional
 *   formData.append("folderId", "<uuid>")    // optional
 *   formData.append("description", "...")    // optional
 *   formData.append("thumbnail", <file>)     // optional
 */
export interface UploadDocumentPayload {
  /**
   * FormData pre-populated by the caller with the file and all metadata fields.
   * The api-client passes it through unchanged, letting the browser / RN runtime
   * set the correct multipart boundary.
   */
  formData: FormData;
}

export interface CreateDocumentFolderPayload {
  entityType: string;
  entityId: string;
  name: string;
  parentFolderId?: string | null;
}

export interface UpdateDocumentMetadataPayload {
  caption?: string | null;
  tags?: string[] | null;
  attachmentType?: string | null;
}

export interface ListDocumentsFilterParams {
  category: string;
  entityType?: string | null;
  entityId?: string | null;
  uploadedById?: string | null;
  attachmentType?: string | null;
}

export class DocumentsApi {
  constructor(private readonly http: HttpClient) {}

  list(
    entityType: string,
    entityId: string,
    category?: string | null,
  ): Promise<ApiResult<DocumentDto[]>> {
    return this.http.get<DocumentDto[]>("/api/documents", {
      query: { entityType, entityId, category: category ?? undefined },
    });
  }

  upload(payload: UploadDocumentPayload): Promise<ApiResult<DocumentDto>> {
    return this.http.post<DocumentDto>("/api/documents", {
      body: payload.formData,
    });
  }

  remove(id: string): Promise<ApiResult<string>> {
    return this.http.delete<string>(`/api/documents/${encodeURIComponent(id)}`);
  }

  updateVisibility(
    id: string,
    visibility: DocumentVisibility,
  ): Promise<ApiResult<DocumentDto>> {
    return this.http.patch<DocumentDto>(`/api/documents/${encodeURIComponent(id)}/visibility`, {
      body: { visibility },
    });
  }

  moveToFolder(
    id: string,
    folderId: string | null,
  ): Promise<ApiResult<DocumentDto>> {
    return this.http.patch<DocumentDto>(`/api/documents/${encodeURIComponent(id)}/folder`, {
      body: { folderId },
    });
  }

  updateMetadata(
    id: string,
    payload: UpdateDocumentMetadataPayload,
  ): Promise<ApiResult<DocumentDto>> {
    return this.http.patch<DocumentDto>(`/api/documents/${encodeURIComponent(id)}/metadata`, {
      body: payload,
    });
  }

  listWithFilter(params: ListDocumentsFilterParams): Promise<ApiResult<DocumentDto[]>> {
    const query: Record<string, string> = { category: params.category };
    if (params.entityType != null) { query.entityType = params.entityType; }
    if (params.entityId != null) { query.entityId = params.entityId; }
    if (params.uploadedById != null) { query.uploadedById = params.uploadedById; }
    if (params.attachmentType != null) { query.attachmentType = params.attachmentType; }
    return this.http.get<DocumentDto[]>('/api/documents/filter', { query });
  }

  // ── Folder sub-resource ──────────────────────────────────────────────────

  listFolders(entityType: string, entityId: string): Promise<ApiResult<DocumentFolderDto[]>> {
    return this.http.get<DocumentFolderDto[]>("/api/documents/folders", {
      query: { entityType, entityId },
    });
  }

  createFolder(payload: CreateDocumentFolderPayload): Promise<ApiResult<DocumentFolderDto>> {
    return this.http.post<DocumentFolderDto>("/api/documents/folders", {
      body: payload,
    });
  }

  deleteFolder(id: string): Promise<ApiResult<string>> {
    return this.http.delete<string>(`/api/documents/folders/${encodeURIComponent(id)}`);
  }
}
